import { AxiosInstance } from "axios"
import {
    FilteredStatuses,
    MeetingServiceOrder,
    MeetingServiceOrdersView,
    TimePeriodMeetingService,
    TransactionResult,
    TransactionLockResult,
} from "./meetingServiceTypes"
import { MeetingDetailsOrder } from "../../../../nonAdminUser/reports/home/meetingServiceApi"
import { IMyOrderDetails } from "../../../../payments/orderTypes"

// Meeting Service Orders

export enum MerchantMeetingServiceOrdersQuery {
    MEETING_SERVICE = "meeting-service-orders-for-merchant",
    ORDERS_SIMPLE = "meeting-service-orders-simple",
    ORDERS_FOR_EXPORT = "meeting-service-orders-for-export",
    TAKEAWAY = "takeaway-orders-for-merchant",
    TAKEAWAY_FOR_EXPORT = "takeaway-orders-for-export",
}

type MeetingServiceOrdersParams = Readonly<{
    merchantId: number
    view?: MeetingServiceOrdersView
    deliveryType?: string
    period?: TimePeriodMeetingService
    orderId?: string
    pendingApproval?: boolean
    invoiceStatus?: string
    isBooked?: string
    startBookedDate?: string
    endBookedDate?: string
    startDeliveryDate?: string
    endDeliveryDate?: string
    customerId?: string
    userGroupId?: string
    email?: string
    paymentMethod?: string
    creditCardLast4?: string
    shopIds?: string
    merchantIds?: string
    satelliteIds?: string
    buyerPartyIds?: string
    deliveryStatus?: string
}>

type MeetingServiceBatchUpdateDeliveryStatusParams = Readonly<{
    updateDeliveryStatus: {
        status: FilteredStatuses
        orderIds: number[]
        message?: string
    }
    isTakeaway?: boolean
}>

export type MeetingServiceBatchUpdateDeliveryStatusResponse = Readonly<{
    orders: TransactionResult[]
}>

export async function batchUpdateDeliveryStatus(
    params: MeetingServiceBatchUpdateDeliveryStatusParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<MeetingServiceBatchUpdateDeliveryStatusResponse> {
    const { isTakeaway, ...body } = params
    const url = isTakeaway ? `/orders/deliveryStatus` : `/meetingService/orders/deliveryStatus`

    const { data } = await axios.post(url, body, {
        signal,
    })

    return data
}

export type MeetingServiceOrdersResponse = Readonly<{
    orders: MeetingServiceOrder[]
}>

export async function fetchMeetingServiceOrders(
    params: MeetingServiceOrdersParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<MeetingServiceOrdersResponse> {
    const { merchantId, deliveryType, period, startDeliveryDate, endDeliveryDate, ...rest } = params
    const url = `/merchants/${merchantId}/production/meetingService/orders`

    const { data } = await axios.get(url, {
        signal,
        params: {
            ...rest,
            type: "meeting_service",
            ...(deliveryType ? { deliveryType } : {}),
            ...(startDeliveryDate || endDeliveryDate
                ? { startDeliveryDate, endDeliveryDate }
                : period
                  ? { period }
                  : {}),
        },
    })

    return data
}

type MeetingServiceOrdersForExportParams = Readonly<{
    orderIds?: number[]
    isTakeaway?: boolean
}>

export type MeetingServiceOrdersForExportResponse = Readonly<{
    orders: MeetingDetailsOrder[]
}>

export async function fetchMeetingServiceOrdersForExport(
    params: MeetingServiceOrdersForExportParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<MeetingServiceOrdersForExportResponse> {
    const { orderIds } = params
    const url = `/meetingService/orders/batch/details`

    const { data } = await axios.post(
        url,
        { orderIds },
        {
            signal,
        },
    )

    return data
}
export type PosOrdersForExportResponse = Readonly<{
    orders: IMyOrderDetails[]
}>

export async function fetchPosOrdersForExport(
    params: MeetingServiceOrdersForExportParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<PosOrdersForExportResponse> {
    const { isTakeaway, ...requestParams } = params
    const url = isTakeaway ? `/orders/batch/details` : `/meetingService/orders/batch/details`

    const { data } = await axios.post(url, requestParams, {
        signal,
    })

    return data
}

type MeetingServiceOrdersSimpleParams = Readonly<{
    merchantId: number
    startDate: string
    endDate: string
}>

export async function fetchMeetingServiceOrdersSimple(
    params: MeetingServiceOrdersSimpleParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<MeetingServiceOrdersResponse> {
    const { merchantId, ...rest } = params
    const url = `/merchants/${merchantId}/production/meetingService/orders`

    const { data } = await axios.get(url, {
        signal,
        params: rest,
    })

    return data
}

// ACTION MENU
type OrderIdsParams = Readonly<{
    orderIds: number[]
}>

type MerchantChangeLockStatusParams = Readonly<{
    updateLockStatus: {
        isLocked: boolean
        orderIds: number[]
    }
}>

type MeetingServiceUpdateLockStatusResponse = Readonly<{
    orders: TransactionLockResult[]
}>

type ServiceResponse = Readonly<{
    orders?: TransactionLockResult[]
    serviceResponse: {
        displayMessage: string
    }
}>

export async function changeReadStatus(
    { orderIds }: OrderIdsParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceResponse> {
    const { data } = await axios.post(
        "/orders/batch/meetingService/readStatus",
        {
            orderIds,
        },
        {
            signal,
        },
    )
    return data
}
export async function changeLockStatus(
    { orderIds }: OrderIdsParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceResponse> {
    const { data } = await axios.post(
        "/orders/batch/meetingService/lockStatus",
        {
            orderIds,
        },
        {
            signal,
        },
    )
    return data
}

export async function changeLockStatusMerchant(
    { updateLockStatus }: MerchantChangeLockStatusParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<MeetingServiceUpdateLockStatusResponse> {
    const { data } = await axios.post(
        "/meetingService/orders/lockStatus",
        {
            updateLockStatus,
        },
        {
            signal,
        },
    )
    return data
}

type UndoBatchStatusUpdateParams = Readonly<{
    updateDeliveryStatus: {
        orderIds: number[]
        undo: boolean
    }
}>

export async function undoBatchStatusUpdate(
    { updateDeliveryStatus }: UndoBatchStatusUpdateParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceResponse> {
    const { data } = await axios.post(
        "/meetingService/orders/deliveryStatus",
        {
            updateDeliveryStatus,
        },
        {
            signal,
        },
    )
    return data
}

type ChangeBookStatusParams = Readonly<{
    orderIds: { id: number; cashPaymentDate?: string }[]
}>

export async function changeBookStatus(
    { orderIds }: ChangeBookStatusParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceResponse> {
    const { data } = await axios.post(
        "/orders/batch/meetingService/book",
        {
            orderIds,
        },
        {
            signal,
        },
    )
    return data
}

type DeleteMeetingServiceOrdersParams = Readonly<{
    orderIds: number[]
    cancelResourceBooking?: boolean
    cancelMeetingService?: boolean
    message?: string
}>

export async function deleteMeetingServiceOrders(
    { orderIds, message, cancelResourceBooking, cancelMeetingService }: DeleteMeetingServiceOrdersParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceResponse> {
    const { data } = await axios.post("/meetingService/orders/delete", {
        deleteOrders: {
            cancelResourceBooking,
            cancelMeetingService,
            orderIds,
            message,
        },
        signal,
    })
    return data
}

type DeleteOrdersParams = Readonly<{
    orderIds: number[]
    cancelResourceBooking: boolean
    cancelMeetingService: boolean
}>

export async function deleteOrders(
    { orderIds, cancelResourceBooking, cancelMeetingService }: DeleteOrdersParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceResponse> {
    const { data } = await axios.delete("/orders/batch", {
        data: {
            cancelMeetingService,
            cancelResourceBooking,
            orderIds,
        },
        signal,
    })
    return data
}

export async function createProductionSlip(
    { orderIds }: OrderIdsParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<Blob> {
    const url = `/orders/batch/meetingService/productionSlip`

    const response = await axios.post(
        url,
        {
            orderIds: orderIds,
        },
        {
            headers: {
                Accept: "application/pdf",
            },
            responseType: "arraybuffer",
            signal,
        },
    )

    const result = new Blob([response.data], {
        type: response.headers["content-type"]?.toString() ?? "application/pdf",
    })

    return result
}

export async function createReceipt(
    { orderIds }: OrderIdsParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<Blob> {
    const url = "/orders/batch/meetingService/receipt"
    const response = await axios.post(
        url,
        {
            orderIds: orderIds,
        },
        {
            headers: {
                Accept: "application/pdf",
            },
            responseType: "arraybuffer",
            signal,
        },
    )

    const result = new Blob([response.data], {
        type: response.headers["content-type"]?.toString() ?? "application/pdf",
    })

    return result
}
